import type { AppProps } from 'next/app';

import { useEffect, useRef, useState } from 'react';
import { createSSRClient } from '~/i18n';
import { I18nextProvider } from 'react-i18next';
import Script from 'next/script';
import { HeroUIProvider, ToastProvider } from "@heroui/react";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { DefaultLayout } from '~/components/layouts/DefaultLayout';
import { UserProvider } from '~/components/contexts/UserContext/UserContext';
import { AuthModalProvider } from '~/components/contexts/AuthModalContext/AuthModalContext';
import { ImageViewerProvider } from '~/components/contexts/ImageViewerContext';
import { CommentsModalProvider } from '~/components/contexts/CommentsModalContext';
import Head from 'next/head';
import { DEFAULT_LOCALE } from '~/locales';
import '~/styles/globals.css';
import { formatHref } from '~/components/atoms/Link/Link';

import { Exo_2 } from 'next/font/google'

const merriweatherSans = Exo_2({
  weight: ['300', '400', '700'],
  subsets: ['latin'],
  display: 'swap',
})

function storePathValues(currentPath: string) {
  if (typeof window === 'undefined' || !sessionStorage) return;

  const prevPath = sessionStorage.getItem('currentPath') ?? '';
  sessionStorage.setItem('prevPath', prevPath);
  sessionStorage.setItem('currentPath', currentPath);
}

/**
 * PWA Service Worker Registration
 * @constructor
 */
function PWAServiceWorker() {
  return (
    <Script
      id="register-sw"
      src="/register-sw.js"
      strategy="afterInteractive"
    />
  );
}

/**
 * Analytics scripts
 * @constructor
 */
function Analytics() {
  return (
    <>
      {/*  Global site tag (gtag.js) - Google Analytics  */}
      <Script
        id="gtag"
        src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GA_ID}`}
        strategy="afterInteractive"
      />
      <Script
        id="google-analytics"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${process.env.NEXT_PUBLIC_GA_ID}');
            `,
        }}
      />
    </>
  );
}

function App({ Component, pageProps, router }: AppProps) {
  const i18n = useRef(createSSRClient(router?.locale || DEFAULT_LOCALE)).current;
  const nextRouter = useRouter();
  const locale = nextRouter.locale || DEFAULT_LOCALE;

  // Create a new QueryClient instance for each request
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // With SSR, we usually want to set some default staleTime
            // above 0 to avoid refetching immediately on the client
            staleTime: 60 * 1000,
          },
        },
      }),
  )

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (typeof window.gtag === 'function') {
        window.gtag('config', process.env.NEXT_PUBLIC_GA_ID, {
          page_path: url,
        });
      }
    };

    router?.events?.on('routeChangeComplete', handleRouteChange);
    return () => {
      router?.events?.off('routeChangeComplete', handleRouteChange);
    };
  }, [router?.events]);

  useEffect(() => {
    if (router?.asPath) {
      storePathValues(router.asPath);
    }
  }, [router?.asPath]);

  // Set up event listeners for route changes to track navigation
  useEffect(() => {
    const handleRouteChangeStart = () => {
      // Store path values before navigation starts
      if (router?.asPath) {
        storePathValues(router.asPath);
      }
    };

    nextRouter.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      nextRouter.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [nextRouter.events, router?.asPath]);

  // Custom navigate function for HeroUIProvider
  const navigate = (href: string, options?: any) => {
    // Store current path before navigation
    if (router?.asPath) {
      storePathValues(router.asPath);
    }

    const formattedHref = formatHref(locale, href);
    return nextRouter.push(
      formattedHref,
      undefined,
      {
        locale: options?.locale || locale,
        shallow: options?.shallow || false,
        ...options
      }
    );
  };

  // Custom useHref function for HeroUIProvider
  const useHref = (href: string) => {
    return formatHref(locale, href);
  };

  return (
    <I18nextProvider i18n={i18n}>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
        <link rel="manifest" href="/site.webmanifest" />
        <meta name="theme-color" content="#ffffff" />
        <link rel="apple-touch-icon" href="/icons/icon-192x192.webp" />
        <link rel="preconnect" href="https://api.bitecritic.com" />
        <link rel="preconnect" href="https://storage.googleapis.com" />
        <link rel="preconnect" href="https://region1.google-analytics.com" />
        <link rel="preconnect" href="https://www.googletagmanager.com" />
      </Head>
      <Analytics />
      <PWAServiceWorker />
      <HeroUIProvider navigate={navigate} useHref={useHref}>
        <ToastProvider />
        <QueryClientProvider client={queryClient}>
          <UserProvider>
            <AuthModalProvider>
              <ImageViewerProvider>
                <CommentsModalProvider>
                  <DefaultLayout className={merriweatherSans.className}>
                    <Component {...pageProps} />
                  </DefaultLayout>
                </CommentsModalProvider>
              </ImageViewerProvider>
            </AuthModalProvider>
          </UserProvider>
        </QueryClientProvider>
      </HeroUIProvider>
    </I18nextProvider>
  );
}
export default App;

